import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import WpaLayout from './WpaLayout';
import { listCartDocs, getCartDocAmount, deleteCartDoc } from './wpaCartStore';
import { collectMoqViolations, buildMoqSubmitErrorMessage } from './wpaProductUtils';
import { formatWpaCommerceApiError } from './wpaCommerceFetch';

const PRIMARY = '#4CAF50';

const styles = {
  section: { padding: '12px 16px', fontSize: 15, fontWeight: 600, color: '#333', borderBottom: '1px solid #eee' },
  orderCard: { margin: '8px 16px', padding: 14, background: '#fff', borderRadius: 10, border: '1px solid #e0e0e0', cursor: 'pointer' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  orderNo: { fontSize: 16, fontWeight: 600, color: '#222' },
  orderDate: { fontSize: 13, color: '#888' },
  amount: { fontSize: 16, color: '#111' },
  status: { fontSize: 13, color: '#666' },
  empty: { textAlign: 'center', padding: 32, color: '#999' },
  bottomBar: { position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, padding: '12px 16px', background: '#fff', borderTop: '1px solid #e0e0e0', zIndex: 900 },
  btn: { width: '100%', height: 48, borderRadius: 24, background: PRIMARY, color: '#fff', border: 'none', fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  btnDisabled: { background: '#ccc', cursor: 'not-allowed' },
  toast: { position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '8px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, whiteSpace: 'nowrap' },
  errorOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 4000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  errorBox: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    maxWidth: 340,
    width: '100%',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  errorTitle: { fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 12 },
  errorBody: { fontSize: 14, color: '#555', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  errorOk: {
    marginTop: 16,
    width: '100%',
    padding: '12px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

function formatDeliveryDate(v) {
  if (!v) return '0000-00-00';
  return String(v).slice(0, 10);
}

export default function WpaCartDocsPage() {
  const { t } = useLanguage();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');
  const [errorDialog, setErrorDialog] = useState(null);

  const load = useCallback(() => {
    setDocs(listCartDocs());
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 1800);
  };

  const submitAll = async () => {
    if (submitting || docs.length === 0) return;
    for (const d of docs) {
      if (!d.lines?.length) continue;
      const violations = collectMoqViolations(d.lines);
      if (!violations.length) continue;
      const base = buildMoqSubmitErrorMessage(violations, t);
      const prefix = d.cartNo ? `${t('wpa.common.moqSubmitDocPrefix', { cartNo: d.cartNo })}\n\n` : '';
      setErrorDialog(prefix + base);
      return;
    }
    setSubmitting(true);
    const token = localStorage.getItem('wpa_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      let submitted = 0;
      const toRemove = [];
      for (const doc of docs) {
        if (!doc.lines || doc.lines.length === 0) continue;
        const customer = JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
        const customerId = doc.customerId || customer?.id;
        if (!customerId) continue;
        const deliveryDateIso = doc.deliveryDate
          ? `${String(doc.deliveryDate).slice(0, 10)}T12:00:00.000Z`
          : null;
        const createRes = await fetch('/api/wpa-commerce/orders', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            customerId,
            clientSubmissionKey: doc.id,
            deliveryDate: deliveryDateIso,
            purchaseOrderNo: doc.poNumber || null,
            remark: doc.remark || null,
            deliveryAddress: doc.address || null,
            lines: doc.lines.map((l) => ({
              productId: l.productId || null,
              itemNo: l.itemNo || '',
              productName: l.name || '',
              unitPrice: Number(l.price || 0),
              quantity: Number(l.quantity || 0),
              unit: l.unit || null,
            })),
          }),
        });
        const createJson = await createRes.json().catch(() => ({}));
        if (createRes.status === 401) {
          navigate(`/${companyId}/wpa/login`, { replace: true });
          return;
        }
        if (!createRes.ok) {
          setErrorDialog(formatWpaCommerceApiError(createRes, createJson, t));
          return;
        }
        const orderId = createJson.data?.id;
        if (!orderId) continue;
        const subRes = await fetch('/api/wpa-commerce/orders/submit', {
          method: 'POST',
          headers,
          body: JSON.stringify({ orderIds: [orderId] }),
        });
        const subJson = await subRes.json().catch(() => ({}));
        if (subRes.status === 401) {
          navigate(`/${companyId}/wpa/login`, { replace: true });
          return;
        }
        if (!subRes.ok) {
          setErrorDialog(formatWpaCommerceApiError(subRes, subJson, t));
          return;
        }
        submitted += 1;
        toRemove.push(doc.id);
      }
      toRemove.forEach((id) => deleteCartDoc(id));
      load();
      showToast(submitted > 0 ? t('wpa.cartDocs.toastSubmitted', { count: submitted }) : t('wpa.cartDocs.toastNothing'));
    } catch (err) {
      setErrorDialog(err?.message || t('wpa.cartDocs.toastFail'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WpaLayout title={t('wpa.titles.cart')}>
      <div style={styles.section}>{t('wpa.cartDocs.section', { count: docs.length })}</div>
      {docs.map((d) => (
        <div key={d.id} style={styles.orderCard} onClick={() => navigate(`/${companyId}/wpa/cart/${d.id}`)}>
          <div style={styles.row}>
            <span style={styles.orderNo}>{d.cartNo}</span>
            <span style={styles.amount} translate="no">${getCartDocAmount(d).toFixed(2)}</span>
          </div>
          <div style={styles.row}>
            <span style={styles.orderDate}>{formatDeliveryDate(d.deliveryDate)}</span>
            <span style={styles.status}>{t('wpa.cartDocs.notSubmitted')}</span>
          </div>
        </div>
      ))}
      {docs.length === 0 && <div style={styles.empty}>{t('wpa.cartDocs.empty')}</div>}
      <div style={{ paddingBottom: 80 }} />
      <div style={styles.bottomBar}>
        <button type="button" style={{ ...styles.btn, ...(docs.length === 0 ? styles.btnDisabled : {}) }} onClick={submitAll} disabled={docs.length === 0 || submitting}>
          {submitting ? t('wpa.cartDocs.submitting') : t('wpa.cartDocs.submitAll')}
        </button>
      </div>
      {toast && <div style={styles.toast}>{toast}</div>}

      {errorDialog && (
        <div style={styles.errorOverlay} role="dialog" aria-modal="true">
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>{t('wpa.cartDetail.errorTitle')}</div>
            <div style={styles.errorBody}>{errorDialog}</div>
            <button type="button" style={styles.errorOk} onClick={() => setErrorDialog(null)}>
              {t('wpa.cartDetail.errorOk')}
            </button>
          </div>
        </div>
      )}
    </WpaLayout>
  );
}
